import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import { Toggle } from "@/components/Toggle/Toggle";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";
import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import {
	useGetPipedriveDealConfigQuery,
	useGetPipedrivePipelinesQuery,
	useGetPipedriveOwnersQuery,
	useGetPipedriveUserMappingsQuery,
	useGetPipedriveCompanyDealCreationTriggerQuery,
	useCreatePipedriveDealMutation,
} from "@/pages/Private/redux/pipedrive/pipedrive.api";

interface ProspectInfo {
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
	title?: string | null;
	organizationName?: string | null;
}

const buildDealName = (template: string | null | undefined, prospect: ProspectInfo): string => {
	const defaultTemplate = "{firstName} {lastName} - {company}";
	let name = template || defaultTemplate;

	name = name.replace("{firstName}", prospect.firstName || "");
	name = name.replace("{lastName}", prospect.lastName || "");
	name = name.replace("{company}", prospect.organizationName || "");
	name = name.replace("{email}", prospect.email || "");
	name = name.replace("{title}", prospect.title || "");

	return name.trim();
};

interface CreatePipedriveDealModalProps {
	isOpen: boolean;
	onClose: () => void;
	prospectId: number;
	prospect?: ProspectInfo;
}

export const CreatePipedriveDealModal: FunctionComponent<CreatePipedriveDealModalProps> = ({
	isOpen,
	onClose,
	prospectId,
	prospect,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.pipedrive.createDeal.${key}`);

	const { user } = useAppSelector(authSelector);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: dealConfig } = useGetPipedriveDealConfigQuery();
	const { data: pipelinesData } = useGetPipedrivePipelinesQuery();
	const { data: ownersData } = useGetPipedriveOwnersQuery();
	const { data: userMappingsData } = useGetPipedriveUserMappingsQuery();
	const { data: dealCreationTrigger } = useGetPipedriveCompanyDealCreationTriggerQuery();
	const [createDeal, { isLoading: isCreating }] = useCreatePipedriveDealMutation();

	const [createAsLead, setCreateAsLead] = useState(false);
	const [dealName, setDealName] = useState("");
	const [pipeline, setPipeline] = useState<string | null>(null);
	const [dealStage, setDealStage] = useState<string | null>(null);
	const [dealOwnerId, setDealOwnerId] = useState<string | null>(null);
	const [closeDate, setCloseDate] = useState("");

	useEffect(() => {
		if (typeof dealCreationTrigger?.createAsLead === "boolean") {
			setCreateAsLead(dealCreationTrigger.createAsLead);
		}
	}, [dealCreationTrigger]);

	useEffect(() => {
		if (dealConfig) {
			if (dealConfig.defaultPipelineId) {
				setPipeline(dealConfig.defaultPipelineId);
			}

			if (dealConfig.defaultStageId) {
				setDealStage(dealConfig.defaultStageId);
			}

			if (dealConfig.defaultOwnerId) {
				setDealOwnerId(dealConfig.defaultOwnerId);
			}

			if (prospect) {
				setDealName(buildDealName(dealConfig.dealNameTemplate, prospect));
			} else if (dealConfig.dealNameTemplate) {
				setDealName(dealConfig.dealNameTemplate);
			}

			if (dealConfig.expectedCloseDateOffset) {
				const date = new Date();

				date.setDate(date.getDate() + dealConfig.expectedCloseDateOffset);
				setCloseDate(date.toISOString().split("T")[0]);
			}
		}
	}, [dealConfig, prospect]);

	useEffect(() => {
		if (userMappingsData && user?.id) {
			const mapping = userMappingsData.find((m) => m.userId === user.id);

			if (mapping) {
				setDealOwnerId(mapping.pipedriveOwnerId);
			}
		}
	}, [userMappingsData, user?.id]);

	const pipelineOptions: DropdownAutoCompleteItem[] = useMemo(
		() => pipelinesData?.pipelines?.map((p) => ({ id: p.id, title: p.label })) || [],
		[pipelinesData]
	);

	const stageOptions: DropdownAutoCompleteItem[] = useMemo(() => {
		if (!pipeline) {
			return [];
		}

		const selectedPipeline = pipelinesData?.pipelines?.find((p) => p.id === pipeline);

		return selectedPipeline?.stages?.map((s) => ({ id: s.id, title: s.label })) || [];
	}, [pipelinesData, pipeline]);

	const ownerOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			ownersData?.owners?.map((o) => ({
				id: o.id,
				title: o.name ? `${o.name} (${o.email})` : o.email,
			})) || [],
		[ownersData]
	);

	const canSubmit = !!prospectId;

	const handleSubmit = async () => {
		if (!canSubmit) {
			return;
		}

		try {
			const result = await createDeal({
				prospectId,
				createAsLead,
				dealName: dealName.trim() || undefined,
				pipeline: createAsLead ? undefined : pipeline || undefined,
				dealStage: createAsLead ? undefined : dealStage || undefined,
				dealOwnerId: dealOwnerId || undefined,
				closeDate: createAsLead ? undefined : closeDate || undefined,
			}).unwrap();

			if (result.crmDeal.url) {
				handleOpenSnackbar(`${ts("success")} — ${result.crmDeal.url}`);
			} else {
				handleOpenSnackbar(ts("success"));
			}

			onClose();
		} catch {
			handleOpenSnackbar(ts("error"));
		}
	};

	return (
		<Modal
			disableSave={!canSubmit}
			handleClose={onClose}
			handleSave={handleSubmit}
			isLoading={isCreating}
			isOpened={isOpen}
			size="sm"
			submitButtonText={createAsLead ? ts("submitLead") : ts("submitDeal")}
			title={createAsLead ? ts("titleLead") : ts("titleDeal")}
		>
			{renderMessageSnackbar()}
			<div className="grid grid-cols-1 gap-4">
				<div className="flex flex-col items-start gap-3">
					<span className="text-sm font-semibold text-text-meta">{ts("createAsLead")}</span>
					<div className="flex flex-row items-center gap-2">
						<Toggle
							isChecked={createAsLead}
							name="createAsLead"
							onChange={() => setCreateAsLead((prev) => !prev)}
						/>

						<span className="text-xs text-text-meta">{ts("createAsLeadHelp")}</span>
					</div>
				</div>

				<InputField
					handleChange={(e) => setDealName(e.target.value)}
					label={createAsLead ? ts("leadTitle") : ts("dealName")}
					name="dealName"
					placeholder={createAsLead ? ts("leadTitle") : ts("dealName")}
					value={dealName}
				/>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					{!createAsLead && (
						<>
							<Dropdown
								data={pipelineOptions}
								defaultValue={pipeline ? pipelineOptions.find((p) => p.id === pipeline) : undefined}
								floating={true}
								handleSelect={(value) => {
									setPipeline(value.id as string);
									setDealStage(null);
								}}
								label={ts("pipeline")}
							/>

							<Dropdown
								key={pipeline ?? "no-pipeline"}
								data={stageOptions}
								defaultValue={dealStage ? stageOptions.find((s) => s.id === dealStage) : undefined}
								disabled={!pipeline}
								floating={true}
								handleSelect={(value) => {
									setDealStage(value.id as string);
								}}
								label={ts("stage")}
							/>
						</>
					)}

					<Dropdown
						data={ownerOptions}
						defaultValue={
							dealOwnerId
								? ownerOptions.find((o) => String(o.id) === String(dealOwnerId))
								: undefined
						}
						floating={true}
						handleSelect={(value) => {
							setDealOwnerId(value.id as string);
						}}
						isOptional={true}
						label={ts("owner")}
					/>

					{!createAsLead && (
						<InputField
							handleChange={(e) => setCloseDate(e.target.value)}
							isOptional={true}
							label={ts("closeDate")}
							name="closeDate"
							type="date"
							value={closeDate}
						/>
					)}
				</div>
			</div>
		</Modal>
	);
};
